const OVERPUNCH = {
  "{": [1, "0"], A: [1, "1"], B: [1, "2"], C: [1, "3"], D: [1, "4"],
  E: [1, "5"], F: [1, "6"], G: [1, "7"], H: [1, "8"], I: [1, "9"],
  "}": [-1, "0"], J: [-1, "1"], K: [-1, "2"], L: [-1, "3"], M: [-1, "4"],
  N: [-1, "5"], O: [-1, "6"], P: [-1, "7"], Q: [-1, "8"], R: [-1, "9"],
};

function compactWhitespace(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

export function parsePershingSignedNumber(rawValue, decimalPlaces) {
  const raw = String(rawValue || "").trim();
  if (!raw) return null;
  const last = raw.slice(-1);
  const overpunch = OVERPUNCH[last];
  const sign = overpunch?.[0] ?? 1;
  const digits = overpunch ? `${raw.slice(0, -1)}${overpunch[1]}` : raw;
  if (!/^\d+$/.test(digits)) return null;
  return sign * Number(digits) / (10 ** decimalPlaces);
}

function parseUnsignedNumber(rawValue, decimalPlaces) {
  const raw = String(rawValue || "").trim();
  if (!/^\d+$/.test(raw)) return null;
  return Number(raw) / (10 ** decimalPlaces);
}

function parseCcyymmdd(rawValue) {
  const raw = String(rawValue || "").trim();
  if (!/^\d{8}$/.test(raw) || raw === "00000000") return null;
  const iso = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(date.valueOf()) || date.toISOString().slice(0, 10) !== iso ? null : iso;
}

function recordKey(line) {
  return `${line.slice(1, 7)}|${line.slice(7, 16).trim().toUpperCase()}`;
}

export function parseBnyPershingIsca(contents) {
  const lines = String(contents || "").split(/\r?\n/).filter((line) => line.length);
  const header = lines.find((line) => line.startsWith("BOF")) || "";
  const trailer = lines.find((line) => line.startsWith("EOF")) || "";
  const headerDateMatch = header.match(/DATA OF\s+(\d{2})\/(\d{2})\/(\d{4})/);
  const fileDate = headerDateMatch
    ? `${headerDateMatch[3]}-${headerDateMatch[1]}-${headerDateMatch[2]}`
    : null;
  const blocks = new Map();
  const errors = [];

  lines.forEach((line, index) => {
    if (line.startsWith("BOF") || line.startsWith("EOF")) return;
    if (line.length !== 132) {
      errors.push({ line_number: index + 1, code: "invalid_record_length", actual_length: line.length });
      return;
    }
    const type = line[0];
    if (!/^[A-O]$/.test(type)) {
      errors.push({ line_number: index + 1, code: "unknown_record_type", record_type: type });
      return;
    }
    const key = recordKey(line);
    const block = blocks.get(key) || { records: {}, lineNumbers: {} };
    if (block.records[type]) {
      errors.push({ line_number: index + 1, code: "duplicate_record_type", record_type: type, key });
    }
    block.records[type] = line;
    block.lineNumbers[type] = index + 1;
    blocks.set(key, block);
  });

  const rows = [];
  const rejected = [];
  for (const [key, block] of blocks) {
    const base = block.records.F || block.records.J || block.records.C || block.records.A;
    const sequenceNumber = base?.slice(1, 7) || key.split("|")[0];
    const cusip = (base?.slice(7, 16) || key.split("|")[1]).trim().toUpperCase();
    const f = block.records.F;
    const j = block.records.J;
    const c = block.records.C;
    const reasons = [];
    if (!f) reasons.push("missing_record_f");
    if (!j) reasons.push("missing_record_j");
    if (!/^[A-Z0-9]{9}$/.test(cusip)) reasons.push("invalid_cusip");

    const priceF = f ? parsePershingSignedNumber(f.slice(74, 92), 9) : null;
    const priceJ = j ? parseUnsignedNumber(j.slice(20, 38), 9) : null;
    const valuationDate = j ? parseCcyymmdd(j.slice(38, 46)) : null;
    if (priceF == null) reasons.push("invalid_latest_price_f");
    if (priceJ == null) reasons.push("invalid_latest_price_j");
    if (!valuationDate) reasons.push("invalid_latest_price_date");
    if (priceF != null && priceJ != null && Math.abs(priceF - priceJ) > 0.000000001) {
      reasons.push("record_f_j_price_mismatch");
    }

    const parsed = {
      sequence_number: sequenceNumber,
      cusip,
      valuation_date: valuationDate,
      price_mid: priceF,
      price_source: j?.slice(100, 101).trim() || null,
      user_cusip_identifier: c?.slice(121, 123).trim() || null,
      security_description: c ? compactWhitespace(c.slice(21, 121)) : null,
      latest_price_f_raw: f?.slice(74, 92) || null,
      latest_price_j_raw: j?.slice(20, 38) || null,
      source_line_f: block.lineNumbers.F || null,
      source_line_j: block.lineNumbers.J || null,
    };
    if (reasons.length) rejected.push({ ...parsed, reasons });
    else rows.push(parsed);
  }

  const trailerCountMatch = trailer.match(/TOTAL DETAIL RECORDS:\s*(\d+)/);
  const declaredDetailCount = trailerCountMatch ? Number(trailerCountMatch[1]) : null;
  const actualDetailCount = lines.filter((line) => !line.startsWith("BOF") && !line.startsWith("EOF")).length;
  if (!header) errors.push({ code: "missing_header" });
  if (!trailer) errors.push({ code: "missing_trailer" });
  if (declaredDetailCount != null && declaredDetailCount !== actualDetailCount) {
    errors.push({ code: "detail_count_mismatch", declared: declaredDetailCount, actual: actualDetailCount });
  }

  return {
    source_system: "bny_pershing",
    file_type: "ISCA",
    file_date: fileDate,
    declared_detail_count: declaredDetailCount,
    actual_detail_count: actualDetailCount,
    security_block_count: blocks.size,
    rows,
    rejected,
    errors,
  };
}

